import axios from "axios";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";

const colors = {
  red: "#a20000",
  redDark: "#700000",
  redSoft: "#fbebeb",
  ink: "#171717",
  muted: "#666666",
  line: "#e8e8e8",
  surface: "#f7f7f7",
  white: "#ffffff",
};

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

const container = (max = 1200) => ({
  width: "100%",
  maxWidth: max,
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: 20,
  paddingRight: 20,
});

const btnBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 9,
  padding: "13px 20px",
  borderRadius: 999,
  fontWeight: 800,
  border: "1px solid transparent",
  cursor: "pointer",
  textDecoration: "none",
  fontSize: 15,
};

const btnPrimary = {
  ...btnBase,
  background: colors.red,
  color: "#fff",
  boxShadow: "0 10px 24px rgba(162,0,0,.24)",
};

const btnSecondary = {
  ...btnBase,
  borderColor: colors.red,
  color: colors.red,
  background: "#fff",
};

const card = {
  background: "#fff",
  border: `1px solid ${colors.line}`,
  borderRadius: 22,
  padding: 26,
  boxShadow: "0 10px 28px rgba(0,0,0,.045)",
};

const specListItem = {
  padding: "11px 0",
  borderBottom: `1px solid ${colors.line}`,
};

const specStrong = {
  display: "block",
  color: colors.red,
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: 0.3,
};

const kicker = {
  color: colors.red,
  fontSize: 13,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 1,
};

const eyebrow = {
  display: "inline-flex",
  padding: "7px 12px",
  borderRadius: 999,
  background: colors.redSoft,
  color: colors.red,
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: 0.8,
  textTransform: "uppercase",
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${colors.line}` }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: "none",
          border: 0,
          padding: "20px 0",
          textAlign: "left",
          font: "inherit",
          fontWeight: 850,
          fontSize: 18,
          display: "flex",
          justifyContent: "space-between",
          cursor: "pointer",
          color: colors.ink,
        }}
      >
        {q}
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 0 20px", color: colors.muted }}>{a}</div>
      )}
    </div>
  );
}

const inverterFaqs = [
  {
    q: "What is an on-grid inverter?",
    a: "It converts solar DC electricity into grid-compatible AC electricity for connected loads and export, where permitted.",
  },
  {
    q: "Can performance be monitored remotely?",
    a: "The catalogue lists Wi-Fi and RS485 communication for both models.",
  },
  {
    q: "Will it work during a grid outage?",
    a: "Standard on-grid inverters normally disconnect during grid outages for anti-islanding safety unless used in an approved backup architecture.",
  },
];

const overviewCards = [
  { icon: "↗", title: "High Efficiency", text: "Maximum efficiency of up to 97.5% and MPPT efficiency above 99%." },
  { icon: "⌁", title: "Remote Monitoring", text: "Wi-Fi and RS485 communication for compatible monitoring systems." },
  { icon: "☂", title: "IP65 Enclosure", text: "Protection against dust and water ingress for suitable installation conditions." },
  { icon: "◌", title: "Quiet Operation", text: "Natural-convection cooling with operating noise below 25 dB." },
  { icon: "▣", title: "Compact Design", text: "Wall-mounted construction for easier handling and installation." },
  { icon: "✓", title: "Grid Ready", text: "Single-phase on-grid architecture for residential rooftop applications." },
];

const applicationCards = [
  { title: "Residential Rooftops", text: "For independent houses and residential buildings." },
  { title: "PM Surya Ghar Installations", text: "For appropriately designed systems, subject to current scheme and DISCOM requirements." },
  { title: "Shops and Offices", text: "For properties with regular daytime electricity consumption." },
  { title: "Clinics and Institutions", text: "For smaller facilities seeking rooftop solar savings." },
  { title: "Retail Outlets", text: "Efficient solar conversion for daily commercial loads." },
  { title: "Small Commercial Properties", text: "Suitable for appropriately sized single-phase systems." },
];

const specRows = [
  ["Rated AC output power", "3 kW", "5 kW"],
  ["Maximum DC input power", "3.9 kW", "6.5 kW"],
  ["Maximum DC input voltage", "550 V", "550 V"],
  ["Start-up voltage", "80 V", "80 V"],
  ["MPPT operating range", "70–500 V", "70–500 V"],
  ["Maximum DC input current", "20 A", "20 A"],
  ["Maximum AC apparent power", "3.3 kVA", "5.5 kVA"],
  ["Maximum efficiency", "97.5%", "97.5%"],
  ["Euro efficiency", "97.3%", "97.3%"],
  ["Communication", "Wi-Fi / RS485", "Wi-Fi / RS485"],
  ["Dimensions", "290 × 290 × 120 mm", "347 × 386 × 167 mm"],
  ["Weight", "≤5 kg", "9 kg"],
];

export default function InverterPage() {
  const width = useViewportWidth();
  const isTablet = width <= 980;
  const isMobile = width <= 640;
  const [menuOpen, setMenuOpen] = useState(false);

  const heroGridCols = isTablet ? "1fr" : "1.05fr .95fr";
  const gridColsN = (n) => (isMobile ? "1fr" : isTablet ? `repeat(${Math.min(n, 2)},1fr)` : `repeat(${n},1fr)`);
  const productGridCols = isTablet ? "1fr" : ".9fr 1.1fr";
  const specGridCols = isMobile ? "1fr" : "repeat(2,1fr)";
  const formGridCols = isMobile ? "1fr" : "repeat(2,1fr)";

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    model: "",
    remark: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      alert('Enter a valid email address')
      return;
    }
    if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s+/g, ""))) {
      alert('Enter a valid 10-digit mobile number')
      return;
    }

    if (formData?.model == '') {
      alert('Select a model')
      return;

    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!validate()) return;
      setIsSubmitting(true);


      let utm = localStorage.getItem('utm_data') || null;

      let dataVal = {
        ...formData,
        utm
      }

      // wire this up to your backend
      const res = await axios.post('https://gautamsolar.us/submit-inverter', dataVal);

      if (res?.data?.success) {
        alert('Form submitted successfully!');
        localStorage.removeItem('utm_data');
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          model: "",
          remark: "",
        })
      }
      setIsSubmitting(false);
    } catch (er) {
      console.log(er)
    }
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "Inter, Arial, Helvetica, sans-serif", color: colors.ink, background: "#fff", lineHeight: 1.6 }}>

      <Helmet>
        <title>Solar Inverters | 3kW & 5kW On-Grid Inverters - Gautam Solar</title>
        <meta
          name="description"
          content="Gautam Solar's GSPL-3000XT and GSPL-5000XT single-phase MPPT on-grid inverters offer up to 97.5% efficiency, IP65 protection, and Wi-Fi/RS485 monitoring for residential and small commercial rooftop solar systems."
        />
        <meta
          name="keywords"
          content="solar inverter, on-grid inverter, MPPT inverter, 3kW inverter, 5kW inverter, residential solar inverter India"
        />
        <link rel="canonical" href="https://gautamsolar.com/inverter/" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Gautam Solar GSPL Series On-Grid Inverters",
            description:
              "Single-phase MPPT on-grid inverters available in 3kW (GSPL-3000XT) and 5kW (GSPL-5000XT) models, with up to 97.5% maximum efficiency, IP65 protection, and Wi-Fi/RS485 remote monitoring.",
            brand: {
              "@type": "Brand",
              name: "Gautam Solar",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Gautam Solar",
              url: "https://gautamsolar.com/",
            },
            category: "Solar Inverter",
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "GSPL-3000XT Rated AC Output",
                value: "3 kW",
              },
              {
                "@type": "PropertyValue",
                name: "GSPL-5000XT Rated AC Output",
                value: "5 kW",
              },
              {
                "@type": "PropertyValue",
                name: "Maximum Efficiency",
                value: "97.5%",
              },
              {
                "@type": "PropertyValue",
                name: "Protection Rating",
                value: "IP65",
              },
              {
                "@type": "PropertyValue",
                name: "Communication",
                value: "Wi-Fi / RS485",
              },
            ],
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: inverterFaqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
              },
            })),
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <header style={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg,#fff 0%,#fff 48%,#fff3f4 100%)", padding: isMobile ? "58px 0 48px" : "40px 0 68px" }}>
        <div style={{ ...container(), display: "grid", gridTemplateColumns: heroGridCols, alignItems: "center", gap: 56, position: "relative", zIndex: 1 }}>
          <div>
            <span style={eyebrow}>Residential Inverter Series</span>
            <h1 style={{ fontSize: "clamp(42px,5.5vw,72px)", lineHeight: 1.04, margin: "18px 0", letterSpacing: -2 }}>
              Smart Solar Inverters for Smarter Energy
            </h1>
            <h2 style={{ fontSize: "clamp(22px,2.4vw,34px)", lineHeight: 1.25, margin: "0 0 16px" }}>
              3 kW & 5 kW Single-Phase MPPT On-Grid Inverters
            </h2>
            <p style={{ fontSize: 18, color: colors.muted, maxWidth: 720 }}>
              Efficient, compact and connected inverters designed for residential and small commercial rooftop solar systems.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
              <a href="#models" style={btnPrimary}>Explore Models</a>
              <a href="#specs" style={btnSecondary}>View Specifications</a>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 26 }}>
              {["Up to 97.5% Efficiency", "IP65 Protection", "Wi-Fi / RS485", "BIS Approved"].map((b) => (
                <span key={b} style={{ background: "#fff", border: `1px solid ${colors.line}`, padding: "9px 12px", borderRadius: 12, fontSize: 13, fontWeight: 800, boxShadow: "0 8px 20px rgba(0,0,0,.04)" }}>
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div style={{ background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 30, padding: 18, boxShadow: "0 18px 48px rgba(24,24,24,.10)", transform: isTablet ? "none" : "rotate(1deg)", maxWidth: isTablet ? 620 : "none", margin: isTablet ? "0 auto" : 0 }}>
            <img src="/inverter1.jpeg" alt="Gautam Solar residential inverter series" style={{ borderRadius: 20, maxHeight: 570, width: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
          </div>
        </div>
      </header>

      {/* Overview */}
      <section id="overview" style={{ padding: isMobile ? "62px 0" : "84px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Product Overview</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Efficient power conversion for rooftop solar systems
            </h2>
            <p style={{ color: colors.muted }}>
              Available in 3 kW and 5 kW models, the series uses MPPT technology to optimise solar generation while supporting remote monitoring and dependable grid-connected operation.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: gridColsN(3), gap: 22 }}>
            {overviewCards.map((c) => (
              <article key={c.title} style={card}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: colors.redSoft, color: colors.red, display: "grid", placeItems: "center", fontSize: 22, fontWeight: 900, marginBottom: 18 }}>
                  {c.icon}
                </div>
                <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>{c.title}</h3>
                <p>{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Models */}
      <section id="models" style={{ padding: isMobile ? "62px 0" : "84px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Product Models</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Choose the right inverter for your rooftop
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: productGridCols, gap: 30, alignItems: "center", background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 28, padding: isMobile ? 20 : 30, boxShadow: "0 18px 48px rgba(24,24,24,.10)" }}>
            <div style={{ background: "linear-gradient(145deg,#fff,#f4f4f4)", border: `1px solid ${colors.line}`, borderRadius: 22, minHeight: 360, display: "grid", placeItems: "start", padding: 18, overflow: "hidden" }}>
              <img src="/inverter3kw.jpeg" alt="GSPL-3000XT and GSPL-5000XT inverter models" style={{ width: '100%', height: '100%', objectFit: "contain", borderRadius: 14 }} />
            </div>
            <div>
              <span style={eyebrow}>3 kW Model</span>
              <h2 style={{ margin: "8px 0" }}>GSPL-3000XT</h2>
              <p style={{ color: colors.muted }}>Compact single-phase inverter for homes and small rooftop systems.</p>
              <div style={{ display: "grid", gridTemplateColumns: specGridCols, gap: "10px 18px", margin: "20px 0" }}>
                <div style={specListItem}><strong style={specStrong}>Rated AC Output</strong>3 kW</div>
                <div style={specListItem}><strong style={specStrong}>Max DC Input</strong>3.9 kW</div>
                <div style={specListItem}><strong style={specStrong}>MPPT Range</strong>70–500 V</div>
                <div style={specListItem}><strong style={specStrong}>Maximum Efficiency</strong>97.5%</div>
                <div style={specListItem}><strong style={specStrong}>Protection</strong>IP65</div>
                <div style={specListItem}><strong style={specStrong}>Weight</strong>≤5 kg</div>
              </div>
              <a href="#enquiry" style={btnPrimary}>Get a Quote</a>
            </div>
          </div>

          <br />

          <div style={{ display: "grid", gridTemplateColumns: productGridCols, gap: 30, alignItems: "center", background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 28, padding: isMobile ? 20 : 30, boxShadow: "0 18px 48px rgba(24,24,24,.10)" }}>
            <div style={{ order: isTablet ? 2 : 1 }}>
              <span style={eyebrow}>5 kW Model</span>
              <h2 style={{ margin: "8px 0" }}>GSPL-5000XT</h2>
              <p style={{ color: colors.muted }}>Higher-capacity inverter for larger homes, shops and small commercial properties.</p>
              <div style={{ display: "grid", gridTemplateColumns: specGridCols, gap: "10px 18px", margin: "20px 0" }}>
                <div style={specListItem}><strong style={specStrong}>Rated AC Output</strong>5 kW</div>
                <div style={specListItem}><strong style={specStrong}>Max DC Input</strong>6.5 kW</div>
                <div style={specListItem}><strong style={specStrong}>MPPT Range</strong>70–500 V</div>
                <div style={specListItem}><strong style={specStrong}>Maximum Efficiency</strong>97.5%</div>
                <div style={specListItem}><strong style={specStrong}>Protection</strong>IP65</div>
                <div style={specListItem}><strong style={specStrong}>Weight</strong>9 kg</div>
              </div>
              <a href="#enquiry" style={btnPrimary}>Get a Quote</a>
            </div>
            <div style={{ order: isTablet ? 1 : 2, background: "linear-gradient(145deg,#fff,#f4f4f4)", border: `1px solid ${colors.line}`, borderRadius: 22, minHeight: 360, padding: 18, overflow: "hidden" }}>

              <img src="/inverter5kw.jpeg" alt="Gautam Solar 5 kW inverter" style={{ width: '100%', height: "100%", objectFit: "contain", borderRadius: 14 }} />

            </div>
          </div>
        </div>
      </section>

      {/* Specs */}
      <section id="specs" style={{ padding: isMobile ? "62px 0" : "84px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Technical Comparison</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              3 kW and 5 kW inverter specifications
            </h2>
          </div>
          <div style={{ overflow: "auto", border: `1px solid ${colors.line}`, borderRadius: 18, background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ padding: "14px 16px", textAlign: "left", background: colors.red, color: "#fff", position: "sticky", top: 0 }}>Parameter</th>
                  <th style={{ padding: "14px 16px", textAlign: "left", background: colors.red, color: "#fff", position: "sticky", top: 0 }}>GSPL-3000XT</th>
                  <th style={{ padding: "14px 16px", textAlign: "left", background: colors.red, color: "#fff", position: "sticky", top: 0 }}>GSPL-5000XT</th>
                </tr>
              </thead>
              <tbody>
                {specRows.map((row, i) => (
                  <tr key={row[0]}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ padding: "14px 16px", borderBottom: i === specRows.length - 1 ? "none" : `1px solid ${colors.line}`, textAlign: "left" }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section id="applications" style={{ padding: isMobile ? "62px 0" : "84px 0", background: colors.redSoft }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Applications</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Ideal for residential and small commercial solar
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: gridColsN(3), gap: 22 }}>
            {applicationCards.map((c) => (
              <div key={c.title} style={card}>
                <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: isMobile ? "62px 0" : "84px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Frequently Asked Questions</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Inverter FAQs
            </h2>
          </div>
          <div style={{ borderTop: `1px solid ${colors.line}` }}>
            {inverterFaqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Enquiry */}
      <section id="enquiry" style={{ padding: isMobile ? "62px 0" : "50px 0" }}>
        <div style={container()}>
          <div style={{ background: `linear-gradient(135deg,${colors.red},${colors.redDark})`, color: "#fff", borderRadius: 30, padding: isMobile ? 28 : 42, display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1.2fr .8fr", gap: 28, alignItems: "center" }}>
            <div>
              <span style={eyebrow}>Product Enquiry</span>
              <h2 style={{ margin: "8px 0 14px", color: "#fff" }}>Find the right inverter for your rooftop</h2>
            </div>
            <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: formGridCols, gap: 14 }}>
              <input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full name"
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Mobile number"
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email address"
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <select
                name="model"
                value={formData.model}
                onChange={handleChange}
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              >
                <option value="">Select model</option>
                <option value="3kW">3 kW</option>
                <option value="5kW">5 kW</option>
                <option value="not-sure">Not sure</option>
              </select>
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                placeholder="Project details"
                style={{ gridColumn: "1/-1", minHeight: 120, resize: "vertical", width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  ...btnSecondary,
                  gridColumn: "1/-1",
                  background: "#fff",
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting && (
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      border: `2px solid ${colors.redSoft}`,
                      borderTopColor: colors.red,
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.7s linear infinite",
                    }}
                  />
                )}
                {isSubmitting ? "Submitting..." : "Sumbit"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
